<script setup>
import { Link, router } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import Pagination from '@/Components/Pagination.vue';
import { ref, watch, onMounted } from "vue";
import debounce from "lodash/debounce";
import { useToast } from 'vue-toast-notification';
import Loading from 'vue-loading-overlay';

const props = defineProps({
    year: String,
    hotels: Object,
    filters: Object,
    flash: Object,
    user: Object,
});

let hotelSearch = ref(props.filters.hotelSearch);
const $toast = useToast();
let isLoading = ref(false);

watch(hotelSearch, debounce(function (value) {
    router.get('/hotels', { hotelSearch: value }, {
        preserveState: true,
        replace: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
}, 700));

onMounted(() => {
    if (props.flash.message) {
        $toast.open({
            message: props.flash.message,
            type: "info",
            duration: 4000,
            position: 'top-right',
            class: 'bs-toast toast bg-dark show'
        });
    }
});

const getInitials = (name) => {
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
    let initials = [...name.matchAll(rgx)] || [];
    initials = (
        (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
    return initials;
};
</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Hotel Manager" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card">
                <div class="card-datatable table-responsive">
                    <div id="DataTables_Table_0_wrapper" class="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div class="card-header flex-column flex-md-row">
                            <div class="head-label text-center">
                                <h5 class="card-title mb-0">Hotel Manager</h5>
                            </div>
                            <div v-if="$page.props.user.permissions.includes('add-hotel')"
                                class="dt-action-buttons text-end pt-3 pt-md-0">
                                <div class="dt-buttons">
                                    <Link type="button" class="btn btn-primary" href="/hotels/create/" preserve-scroll
                                        replace>
                                    <i class='bx bx-plus bx-tada'></i> Add New Hotel
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-sm-12 col-md-12 d-flex justify-content-center justify-content-md-end">
                                <div class="dataTables_filter">
                                    <label>Search:
                                        <input v-model="hotelSearch" type="text" class="form-control"
                                            placeholder="Type hotel name"></label>
                                </div>
                            </div>
                        </div>
                        <table class="datatables-basic table border-top dataTable no-footer dtr-column"
                            id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info"
                            style="width: 1390px;">
                            <thead>
                                <tr role="row">
                                    <th>Name</th>
                                    <th>Star Rating</th>
                                    <th>Type</th>
                                    <th>Country</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="odd" v-for="(hotel, index) in hotels.data" :key="hotel.id">
                                    <td>
                                        <div class="d-flex justify-content-start align-items-center user-name">
                                            <div class="avatar-wrapper">
                                                <div class="avatar me-2"><span
                                                        class="avatar-initial rounded-circle bg-label-danger">{{
                                                            getInitials(hotel.name)
                                                        }}</span>
                                                </div>
                                            </div>
                                            <div class=" d-flex flex-column"><span class="emp_name text-truncate">{{
                                                hotel.name
                                                    }}</span><small class="emp_post text-truncate text-muted">{{
                                                    hotel.city.name
                                                    }}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <i v-for="(n, i) in hotel.star_rating" class="bx bxs-star text-warning"></i>
                                    </td>
                                    <td>{{ hotel.property_type }}</td>
                                    <td>{{ hotel.country.name }}</td>
                                    <td>
                                        <span v-if="hotel.active == 1" class="badge bg-label-success">Active</span>
                                        <span v-else class="badge bg-label-danger">Disabled</span>
                                    </td>
                                    <td>
                                        <Link :href="'hotels/show/' + hotel.id" class="btn btn-icon btn-label-secondary"
                                            preserve-state>
                                        <i class="tf-icons bx bx-search-alt"></i>
                                        </Link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="row">
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_info" id="DataTables_Table_0_info" role="status"
                                    aria-live="polite">Showing {{ hotels.from }} to {{ hotels.to }} of {{
                                        hotels.total
                                    }} Hotels</div>
                            </div>
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_paginate">
                                    <Pagination :links="hotels.links"></Pagination>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>